import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RoleBadge } from "@/components/badges";
import { UserAvatar } from "@/components/Avatar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, UserPlus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/components/ConfirmDialog";

export function UsersTab() {
  const { askConfirm, confirmDialog } = useConfirm();
  const [assignUser, setAssignUser] = useState(null);
  const [assignForm, setAssignForm] = useState({ project_id: "", party_id: "" });
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user: me } = useAuth();

  const { data: users = [] } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });

  const deleteUserMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/users/${id}`),
    onSuccess: () => {
      toast.success(t("admin.toastUserDeleted"));
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["admin-projects"],
    queryFn: async () => (await api.get("/admin/projects")).data,
  });
  const { data: assignParties = [] } = useQuery({
    queryKey: ["assign-parties", assignForm.project_id],
    queryFn: async () => (await api.get(`/projects/${assignForm.project_id}/parties`)).data,
    enabled: !!assignForm.project_id,
  });
  const assignMut = useMutation({
    mutationFn: async () => api.post("/admin/memberships", { user_id: assignUser.id, party_id: assignForm.party_id }),
    onSuccess: () => { toast.success(t("admin.assignToast")); setAssignUser(null); setAssignForm({ project_id: "", party_id: "" }); qc.invalidateQueries(); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const removeMemMut = useMutation({
    mutationFn: async (mid) => api.delete(`/admin/memberships/${mid}`),
    onSuccess: () => { toast.success(t("admin.membershipRemovedToast")); qc.invalidateQueries(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{t("admin.usersCard")}</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader><TableRow><TableHead>{t("admin.thUser")}</TableHead><TableHead>{t("admin.thJobTitle")}</TableHead><TableHead>{t("admin.thPhone")}</TableHead><TableHead>{t("admin.thType")}</TableHead><TableHead>{t("admin.thMemberships")}</TableHead><TableHead className="w-14 text-right"></TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <UserAvatar user={u} testId={`admin-user-avatar-${u.id}`} />
                    <div>
                      <div className="font-medium">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{u.job_title || <span className="text-xs text-slate-400">—</span>}</TableCell>
                <TableCell className="font-mono text-xs" data-testid={`admin-user-phone-${u.id}`}>{u.phone_number ? `${u.phone_country_code || ""} ${u.phone_number}` : "—"}</TableCell>
                <TableCell>{u.is_super_admin ? <RoleBadge role="Super Admin" /> : u.is_admin ? <RoleBadge role="Admin" /> : <Badge variant="outline" className="font-mono text-xs">{t("badges.member")}</Badge>}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {u.memberships.map((m) => (
                      <Badge key={m.id || `${m.project_name}-${m.company_name}-${m.role}`} variant="outline" className="gap-1 font-mono text-[10px]">
                        {m.project_name} · {m.company_name} · {m.role}
                        {(me?.is_super_admin || me?.is_admin) && m.id && (
                          <button type="button" className="text-slate-400 hover:text-rose-600" title={t("admin.removeMembershipT")} onClick={() => askConfirm(t("admin.removeMembershipConfirm", { name: u.name || u.email, company: m.company_name, project: m.project_name }), () => removeMemMut.mutate(m.id))} data-testid={`membership-remove-${m.id}`}>
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    ))}
                    {u.memberships.length === 0 && <span className="text-xs text-slate-400">—</span>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {(me?.is_super_admin || me?.is_admin) && (
                    <Button variant="ghost" size="icon" title={t("admin.assignUserT")} onClick={() => { setAssignUser(u); setAssignForm({ project_id: "", party_id: "" }); }} data-testid={`assign-user-${u.id}`}>
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  )}
                  {me?.is_super_admin && !u.is_super_admin && u.id !== me?.id && (
                    <Button variant="ghost" size="icon" title={t("admin.deleteUserT")} className="text-rose-600" onClick={() => askConfirm(t("admin.deleteUserConfirm", { email: u.email }), () => deleteUserMut.mutate(u.id))} data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <Dialog open={!!assignUser} onOpenChange={(o) => !o && setAssignUser(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>{t("admin.assignTitle", { name: assignUser?.name || assignUser?.email })}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("admin.assignProjectL")}</Label>
              <Select value={assignForm.project_id} onValueChange={(v) => setAssignForm({ project_id: v, party_id: "" })}>
                <SelectTrigger data-testid="assign-project-select"><SelectValue placeholder={t("common.selectProject")} /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`assign-project-${p.id}`}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("admin.assignPartyL")}</Label>
              <Select value={assignForm.party_id} onValueChange={(v) => setAssignForm({ ...assignForm, party_id: v })} disabled={!assignForm.project_id}>
                <SelectTrigger data-testid="assign-party-select"><SelectValue placeholder={t("common.selectParty")} /></SelectTrigger>
                <SelectContent>
                  {assignParties.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`assign-party-${p.id}`}>{p.company_name} ({p.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => assignMut.mutate()} disabled={!assignForm.party_id || assignMut.isPending} data-testid="assign-submit">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </Card>
  );
}
